import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Clock, Loader2, Sparkles, Users } from "lucide-react";
import { orgApi } from "../lib/organizerApi";

export default function OrganizerEventRegistrations() {
  const { slug } = useParams();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [regs, st] = await Promise.all([
          orgApi.get(`/organizer/events/${slug}/registrations`),
          orgApi.get(`/organizer/events/${slug}/stats`),
        ]);
        setRows(regs.data.registrations || []);
        setStats(st.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Không thể tải danh sách.");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  return (
    <div className="min-h-screen bg-linen" data-testid="organizer-registrations">
      <header className="border-b border-espresso/10 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/organizer/dashboard" className="inline-flex items-center gap-2 text-espresso-soft hover:text-espresso text-sm" data-testid="org-back">
            <ArrowLeft className="w-4 h-4" /> Quay lại Dashboard
          </Link>
          <div className="inline-flex items-center gap-2 text-espresso">
            <Sparkles className="w-4 h-4" />
            <span className="tracking-[0.28em] uppercase text-sm">Nail Viet Club</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <p className="text-xs uppercase tracking-[0.28em] text-espresso-muted">Đăng ký sự kiện</p>
        <h1 className="text-3xl sm:text-4xl font-serif text-espresso mt-1">{slug}</h1>

        {stats && (
          <div className="mt-6 grid sm:grid-cols-4 gap-3">
            <Stat label="Tổng đăng ký" value={stats.total} icon={<Users className="w-4 h-4" />} testId="reg-stat-total" />
            <Stat label="Đã check-in" value={stats.checked} icon={<CheckCircle2 className="w-4 h-4" />} testId="reg-stat-checked" />
            <Stat label="Walk-in" value={stats.walkins} icon={<Clock className="w-4 h-4" />} testId="reg-stat-walkins" />
            <Stat label="Tỷ lệ check-in" value={stats.total ? `${Math.round((stats.checked / stats.total) * 100)}%` : "—"} testId="reg-stat-rate" />
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-espresso-soft" data-testid="reg-loading">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : error ? (
          <div className="mt-6 rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">{error}</div>
        ) : rows.length === 0 ? (
          <div className="mt-8 rounded-3xl bg-white border border-espresso/10 p-10 text-center" data-testid="reg-empty">
            <Sparkles className="w-7 h-7 text-espresso/40 mx-auto" />
            <h3 className="mt-3 text-xl font-serif text-espresso">Chưa có đăng ký</h3>
            <p className="text-espresso-soft text-sm mt-1">Hãy chia sẻ link đăng ký để bắt đầu nhận khách.</p>
          </div>
        ) : (
          <div className="mt-8 rounded-3xl bg-white border border-espresso/10 overflow-hidden" data-testid="reg-table">
            <table className="w-full text-left text-sm">
              <thead className="bg-linen-100/60 text-[10px] uppercase tracking-[0.22em] text-espresso-muted">
                <tr>
                  <th className="px-4 py-3">Khách</th>
                  <th className="px-4 py-3 hidden sm:table-cell">Liên hệ</th>
                  <th className="px-4 py-3 hidden md:table-cell">Khách kèm</th>
                  <th className="px-4 py-3">Check-in</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`reg-row-${r.id}`} className="border-t border-espresso/5">
                    <td className="px-4 py-3">
                      <div className="font-medium text-espresso">{r.business_name || r.owner_name || "—"}</div>
                      <div className="text-[11px] text-espresso-muted">{r.owner_name && r.business_name ? r.owner_name : ""}</div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-espresso-soft">
                      <div>{r.email || "—"}</div>
                      <div className="text-[11px]">{r.phone_normalized || r.phone}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-espresso-soft">{r.guest_count || 0}</td>
                    <td className="px-4 py-3">
                      {r.checked_in ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#E1EDE6] text-[#2D4A3E] text-[10px] font-semibold px-2 py-0.5">
                          <CheckCircle2 className="w-3 h-3" /> Đã check-in
                        </span>
                      ) : (
                        <span className="text-[11px] text-espresso-muted">—</span>
                      )}
                      {r.is_walkin && (
                        <span className="ml-1 inline-flex items-center rounded-full bg-[#FBEFD0] text-[#7A5C16] text-[10px] font-semibold px-2 py-0.5">Walk-in</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, icon, testId }) {
  return (
    <div data-testid={testId} className="rounded-2xl bg-white border border-espresso/10 p-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-espresso-muted flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className="text-2xl sm:text-3xl font-serif text-espresso mt-1">{value}</div>
    </div>
  );
}
